import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Loader2, MessageSquare, Flag, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public reviews block for an app detail page.
 * - Lists existing approved reviews with star ratings + publisher replies.
 * - Inline form to submit a new review (rating + title + body + name + email).
 */
export const AppReviews = ({ slug, lang = "nl" }) => {
  const isNl = lang === "nl";
  const [data, setData] = useState({ avg: 0, total: 0, distribution: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rating: 0, title: "", body: "", author_name: "", author_email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [thankYou, setThankYou] = useState(false);

  const refresh = () => {
    setLoading(true);
    axios.get(`${API}/apps/${slug}/reviews`)
      .then((r) => setData(r.data))
      .catch(() => setData({ avg: 0, total: 0, distribution: {}, items: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.rating) { setError(isNl ? "Geef een sterren-score" : "Pick a star rating"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/apps/${slug}/reviews`, form);
      setThankYou(true);
      setForm({ rating: 0, title: "", body: "", author_name: "", author_email: "" });
      setShowForm(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const flag = async (rid) => {
    if (!window.confirm(isNl ? "Deze review als ongepast melden?" : "Report this review as inappropriate?")) return;
    try { await axios.post(`${API}/apps/${slug}/reviews/${rid}/flag`); refresh(); } catch (_) {}
  };

  return (
    <section className="hac-container py-12 max-w-4xl" data-testid="app-reviews-section">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="overline mb-2">{isNl ? "Gebruikers reviews" : "User reviews"}</div>
          <h2 className="font-heading text-3xl text-[#0F172A] leading-tight">
            {isNl ? "Wat ruiters zelf zeggen" : "What riders say themselves"}
          </h2>
          {data.total > 0 && (
            <div className="mt-3 flex items-center gap-3" data-testid="reviews-summary">
              <div className="font-heading text-4xl text-[#0F172A]">{data.avg}</div>
              <div>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={16} className={n <= Math.round(data.avg) ? "fill-amber-500 text-amber-500" : "text-[#CBD5E1]"} />
                  ))}
                </div>
                <div className="text-xs text-[#64748B] mt-0.5">{data.total} {isNl ? "reviews" : "reviews"}</div>
              </div>
            </div>
          )}
        </div>
        <button onClick={() => { setShowForm(!showForm); setThankYou(false); }} className="hac-btn-primary" data-testid="reviews-write-btn">
          <MessageSquare size={14} /> {isNl ? "Schrijf een review" : "Write a review"}
        </button>
      </div>

      {thankYou && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-emerald-800 inline-flex items-start gap-2" data-testid="reviews-thank-you">
          <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
          <div>
            <strong>{isNl ? "Bedankt voor je review!" : "Thanks for your review!"}</strong>
            <p className="text-sm">{isNl ? "Je review is geplaatst en zichtbaar voor alle bezoekers." : "Your review is live and visible to all visitors."}</p>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mb-8 bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3" data-testid="review-form">
          <div>
            <label className="overline block mb-1">{isNl ? "Sterren" : "Rating"} *</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}>
                  <Star size={32} className={n <= form.rating ? "fill-amber-500 text-amber-500" : "text-[#CBD5E1] hover:text-amber-300"} />
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">{isNl ? "Je naam" : "Your name"} *</label>
              <input className="hac-input" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} required minLength={2} maxLength={60} data-testid="review-name" />
            </div>
            <div>
              <label className="overline block mb-1">Email *</label>
              <input type="email" className="hac-input" value={form.author_email} onChange={(e) => setForm({ ...form, author_email: e.target.value })} required data-testid="review-email" />
              <div className="text-[10px] text-[#94A3B8] mt-1">{isNl ? "Niet getoond, alleen voor spam-bescherming" : "Not shown publicly, only for spam protection"}</div>
            </div>
          </div>
          <div>
            <label className="overline block mb-1">{isNl ? "Titel (optioneel)" : "Title (optional)"}</label>
            <input className="hac-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120} data-testid="review-title" />
          </div>
          <div>
            <label className="overline block mb-1">{isNl ? "Je ervaring" : "Your experience"} *</label>
            <textarea className="hac-input min-h-[120px]" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required minLength={20} maxLength={2000} placeholder={isNl ? "Wat werkt goed, wat kan beter?" : "What works well, what could be better?"} data-testid="review-body" />
            <div className="text-[10px] text-[#94A3B8] mt-1">{form.body.length}/2000</div>
          </div>
          {error && <div className="text-sm text-red-600" data-testid="review-error">{error}</div>}
          <div className="flex gap-2">
            <button type="submit" disabled={submitting} className="hac-btn-primary" data-testid="review-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : isNl ? "Plaats review" : "Post review"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="hac-btn-secondary">{isNl ? "Annuleren" : "Cancel"}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-8 text-[#94A3B8]"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#CBD5E1] rounded-2xl bg-[#F8FAFC]">
          <MessageSquare size={32} className="mx-auto text-[#94A3B8] mb-2" />
          <p className="text-sm text-[#64748B]">{isNl ? "Nog geen reviews — wees de eerste!" : "No reviews yet — be the first!"}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.items.map((r) => (
            <article key={r.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={14} className={n <= r.rating ? "fill-amber-500 text-amber-500" : "text-[#CBD5E1]"} />
                      ))}
                    </div>
                    <span className="text-xs text-[#64748B]">{r.author_name}</span>
                    <span className="text-xs text-[#CBD5E1]">·</span>
                    <span className="text-xs text-[#94A3B8]">{new Date(r.created_at).toLocaleDateString(isNl ? "nl-NL" : "en-US")}</span>
                  </div>
                  {r.title && <h3 className="font-bold text-[#0F172A] mt-1">{r.title}</h3>}
                </div>
                <button onClick={() => flag(r.id)} className="text-[#CBD5E1] hover:text-red-500 p-1" title={isNl ? "Meld als ongepast" : "Report"} data-testid={`review-flag-${r.id}`}>
                  <Flag size={13} />
                </button>
              </div>
              <p className="text-[#0F172A] leading-relaxed whitespace-pre-wrap">{r.body}</p>
              {(r.replies || []).length > 0 && (
                <div className="mt-4 ml-4 pl-4 border-l-4 border-[#2563EB] space-y-3" data-testid={`review-replies-${r.id}`}>
                  {r.replies.map((rep) => (
                    <div key={rep.id} className="bg-[#EFF6FF] rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#2563EB] bg-white border border-[#DBEAFE] px-1.5 py-0.5 rounded">
                          ✓ {isNl ? "Reactie van" : "Reply from"} {rep.publisher_name}
                        </span>
                        <span className="text-[10px] text-[#94A3B8]">{new Date(rep.created_at).toLocaleDateString(isNl ? "nl-NL" : "en-US")}</span>
                      </div>
                      <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{rep.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default AppReviews;
